/**
 * Asoebi packing assignments: /planning/:project_id/asoebi/packing-assignments*
 * (ans-backend, kal shape). No cache: screens load through useLoad so a failed
 * request is an error box, never an empty list. The server derives status from
 * the packing, so nothing here sends one except for an assignment without a
 * checklist.
 */
import { apiGet, apiPost, apiPatch, apiDelete, planningPath } from './PlanningApiClient';
import type {
  AsoebiAssignment, AsoebiAssignmentDetail, AssignmentInput, AssignmentSummary, AssignmentUpdate, BulkPackResponse,
} from '../types/ui/AsoebiAssignment';

const base = '/asoebi/packing-assignments';
const query = (archived: boolean) => (archived ? '?archived=true' : '');
const one = (id: string) => `${base}/${encodeURIComponent(id)}`;

export async function fetchAssignments(archived = false): Promise<AsoebiAssignment[]> {
  const res = await apiGet<{ assignments: AsoebiAssignment[] }>(planningPath(`${base}${query(archived)}`));
  return res.assignments;
}

export const fetchAssignmentSummary = (archived = false): Promise<AssignmentSummary> =>
  apiGet<AssignmentSummary>(planningPath(`${base}/summary${query(archived)}`));

export const fetchAssignment = (id: string): Promise<AsoebiAssignmentDetail> =>
  apiGet<AsoebiAssignmentDetail>(planningPath(one(id)));

export const createAssignment = (input: AssignmentInput): Promise<AsoebiAssignmentDetail> =>
  apiPost<AsoebiAssignmentDetail>(planningPath(base), input);

export const updateAssignment = (id: string, patch: AssignmentUpdate): Promise<AsoebiAssignmentDetail> =>
  apiPatch<AsoebiAssignmentDetail>(planningPath(one(id)), patch);

export const deleteAssignment = (id: string): Promise<{ message: string }> =>
  apiDelete<{ message: string }>(planningPath(one(id)));

/** Packs one checklist line (takes its quantity out of stock). Packing a packed line changes nothing. */
export const packLine = (id: string, productId: string): Promise<AsoebiAssignmentDetail> =>
  apiPost<AsoebiAssignmentDetail>(planningPath(`${one(id)}/pack`), { product_id: productId });

export const unpackLine = (id: string, productId: string): Promise<AsoebiAssignmentDetail> =>
  apiPost<AsoebiAssignmentDetail>(planningPath(`${one(id)}/unpack`), { product_id: productId });

/** Marks every given assignment packed. One failing never undoes the others: read `results`. */
export const bulkPack = (ids: readonly string[]): Promise<BulkPackResponse> =>
  apiPost<BulkPackResponse>(planningPath(`${base}/bulk-pack`), { ids });
